import { AlertBanner, AlertBannerText, Loader } from "monday-ui-react-core";
import type { Dispatch, SetStateAction } from "react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { useCurrentMondayContext } from "@/components/AppContext/WithMondayContextAvailable";
import type { Groups } from "@/components/BacklogTable/hooks";
import { useQueryItemsForGroups } from "@/components/BacklogTable/hooks";
import type { ErrorWithData } from "@/lib/error";
import { getErrorMessage } from "@/lib/error";
import { MAX_ALLOWED_ITEMS } from "@/services/constant";
import { getGroups, getMondayComplexity } from "@/services/monday-service";
import type { BoardGroup } from "@/types/common";
import { CheckOption } from "@/types/common";
import { sleep } from "@/utils/sleep";

import { InfiniteLoader } from "./InfiniteLoader";
import { ItemsTable } from "./ItemsTable";

const COMPLEXITY_RESET_DELAY = 5;

interface GroupListProps {
  selectedItems: string[];
  setSelectedItems: Dispatch<SetStateAction<string[]>>;
  allBoardItemsFlag: CheckOption;
  setAllBoardItemsFlag: Dispatch<SetStateAction<CheckOption>>;
  includedItems: string[];
}

export function GroupList({
  setSelectedItems,
  selectedItems,
  allBoardItemsFlag,
  setAllBoardItemsFlag,
  includedItems,
}: GroupListProps) {
  const onSelectChange = useCallback(
    ({ selected, unselected }: { selected: string[]; unselected: string[] }) => {
      return setSelectedItems((current) =>
        [...new Set([...current, ...selected])].filter((id) => !unselected.includes(id))
      );
    },
    [setSelectedItems]
  );
  const mondayContext = useCurrentMondayContext();
  const { boardId } = mondayContext;
  const [groups, setGroups] = useState<BoardGroup[]>([]);
  const [loading, setLoading] = useState<boolean>();
  const { grouped, loadNextPage, hasMore, loading: itemsLoading } = useQueryItemsForGroups(boardId, groups);
  const isNonEmpty =
    Object.values(grouped)
      .map((g) => g.items)
      .flat().length > 0;

  const highlightedItemId = "undefined";
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        const groups = await getGroups(boardId);
        setGroups(groups);
      } catch (error) {
        if ((error as ErrorWithData).data?.errors?.length > 0) {
          const retryDelay = (await getMondayComplexity("query")).reset_in_x_seconds;
          await sleep(retryDelay + COMPLEXITY_RESET_DELAY);
          const groups = await getGroups(boardId);
          setGroups(groups);
        } else {
          setErrorMessage(getErrorMessage(error));
        }
      } finally {
        setLoading(false);
      }
    })()
      .then(() => {})
      .catch(() => {});
  }, [boardId]);

  const allItemIdArray = React.useMemo<string[]>(() => {
    return Object.values(grouped).reduce((prev: string[], group) => {
      const value = Object.values(group.items).map((item) => item.id);
      return [...prev, ...value];
    }, []);
  }, [JSON.stringify(grouped)]);

  useEffect(() => {
    switch (allBoardItemsFlag) {
      case CheckOption.NONE:
        if (
          selectedItems.length &&
          allItemIdArray.length &&
          groups.length &&
          groups.length === Object.keys(grouped).length &&
          allItemIdArray.length === selectedItems.length
        ) {
          setAllBoardItemsFlag(CheckOption.TRUE);
        }
        break;
      case CheckOption.FALSE:
        setSelectedItems([]);
        break;
      case CheckOption.TRUE:
        setSelectedItems(allItemIdArray);
        break;
    }
  }, [allBoardItemsFlag, JSON.stringify(allItemIdArray)]);

  const selectedItemIds = useMemo(
    () =>
      selectedItems.reduce<{ [key: string]: boolean }>((ids, key) => {
        ids[key] = true;
        return ids;
      }, {}),
    [JSON.stringify(selectedItems)]
  );

  const filtered = Object.entries(grouped).reduce((all, [id, group]) => {
    const items = group.items.filter((i) =>
      includedItems.length > 0 ? includedItems.includes(i.id.toString()) : true
    );
    return { ...all, [id]: { ...group, items } };
  }, {});

  if (groups.length === 0 && loading) {
    return (
      <Center>
        <Loader size={Loader.sizes.MEDIUM} />
      </Center>
    );
  }

  if (groups.length === 0 && errorMessage) {
    const errorText = errorMessage ? `: ${errorMessage}` : "";
    return (
      <Center>
        <div>{`An error has occurred${errorText}`}</div>
      </Center>
    );
  }

  return (
    <GroupsWrapper id="groups">
      {selectedItems.length > MAX_ALLOWED_ITEMS && (
        <AlertBanner backgroundColor={AlertBanner.backgroundColors?.NEGATIVE} isCloseHidden={true}>
          <AlertBannerText text="You can select only up to 500 items on a board to clone." />
        </AlertBanner>
      )}
      {Object.entries(filtered as Groups).map(
        ([id, group]) =>
          group.items &&
          group.items.length > 0 && (
            <ItemsTable
              key={id}
              selectedItemIds={selectedItemIds}
              isRowSelectionEnabled={true}
              items={group.items}
              group={group}
              onSelectChange={onSelectChange}
              highlightedItemId={highlightedItemId}
            />
          )
      )}
      {isNonEmpty && <InfiniteLoader loading={itemsLoading} loadNextPage={loadNextPage} hasMore={hasMore} />}
    </GroupsWrapper>
  );
}

const GroupsWrapper = styled.div`
  overflow: auto;
  width: 100%;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;
